// travel.js
// Travel Checklist page -- lets users add, check off, and delete packing items.
//
// ── NEW FEATURES ─────────────────────────────────────────────
//   STANDARD LIST  - Save a reusable template of items you always pack.
//                    Stored in localStorage so it persists between sessions
//                    without needing a backend schema change.
//                    "Save as Standard" snapshots the current list.
//                    "Load Standard"    adds template items not already present.
//
//   UNCHECK ALL    - Resets every item to not-completed so you can reuse
//                    the same list as a fresh packing checklist.
//                    Requires one confirmation click to prevent accidents.
//
//   DELETE ALL     - Removes every item from the list after confirmation.
//                    Useful for starting completely fresh before a trip.
// ─────────────────────────────────────────────────────────────

import { useState } from 'react';
import { useTravelItems } from '../hooks/useTravelItems';
// useTravelItems = custom hook for all Supabase travel-item operations.
// This component now expects the hook to also expose:
//   uncheckAll()     - sets completed = false on every item in DB + state
//   deleteAllItems() - deletes every item from DB + clears state
// Add those two functions to useTravelItems.js if not already present.

// ── STORAGE KEY ───────────────────────────────────────────────
// localStorage key where the standard list template is saved.
// Namespaced to avoid collisions with other localStorage users.
const STANDARD_LIST_KEY = 'dpp_standard_travel_items'

function Travel() {

  // ── HOOK ─────────────────────────────────────────────────────
  const {
    travelItems,
    loading,
    addItem,
    deleteItem,
    toggleCompleted,
    uncheckAll,      // NEW: sets all items completed = false
    deleteAllItems,  // NEW: removes all items from DB and state
  } = useTravelItems()


  // ── LOCAL INPUT STATE ─────────────────────────────────────────
  const [inputValue, setInputValue] = useState('')
  // inputValue = what the user is currently typing in the add-item input


  // ── STANDARD LIST STATE ───────────────────────────────────────
  const [showStandardPanel, setShowStandardPanel] = useState(false)
  // true = standard list management panel is visible below the main list

  // Load the saved standard list from localStorage on first read.
  // JSON.parse returns null if nothing is stored, so we default to [].
  const getSavedStandard = () => {
    try {
      return JSON.parse(localStorage.getItem(STANDARD_LIST_KEY)) || []
      // JSON.parse converts the stored JSON string back into an array
    } catch {
      return []
      // if the stored value is corrupted, start fresh
    }
  }

  const [standardItems, setStandardItems] = useState(getSavedStandard)
  // standardItems = array of name strings, e.g. ["Wig", "Corset", "Heels"]
  // This is just the TEMPLATE list, not the live packing items.


  // ── UNCHECK CONFIRMATION STATE ────────────────────────────────
  const [confirmUncheck, setConfirmUncheck] = useState(false)
  // two-step uncheck: first click shows the confirm button, second executes
  // prevents accidental resets during packing

  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false)
  // same two-step pattern for delete all


  // ── ACTION HANDLERS ───────────────────────────────────────────

  const handleAdd = () => {
    // Adds a new item to the live packing list.

    if (inputValue.trim() === '') return
    // .trim() strips accidental leading/trailing spaces
    // empty input = do nothing

    addItem(inputValue.trim())
    // save to Supabase and update local state via the hook

    setInputValue('')
    // clear the input box after adding
  }

  const handleKeyDown = (e) => {
    // Lets user press Enter instead of clicking the Add button.
    if (e.key === 'Enter') handleAdd()
  }


  // ── UNCHECK ALL ───────────────────────────────────────────────

  const handleUncheckAll = async () => {
    // First click: show the confirm button.
    if (!confirmUncheck) {
      setConfirmUncheck(true)
      // flip to confirm state -- UI will swap the button to a red "Confirm Reset" button
      return
    }

    // Second click (confirmed): execute the reset.
    try {
      await uncheckAll()
      // calls useTravelItems.uncheckAll() which does a Supabase UPDATE
      // setting completed = false for all of this user's travel items
    } catch (err) {
      alert('Error resetting checklist: ' + (err.message || 'Unknown error'))
    } finally {
      setConfirmUncheck(false)
      // always return to the normal (non-confirm) state after trying
    }
  }

  const cancelUncheck = () => {
    // Cancels the two-step uncheck flow without doing anything.
    setConfirmUncheck(false)
  }


  // ── DELETE ALL ────────────────────────────────────────────────

  const handleDeleteAll = async () => {
    // Same two-step pattern as uncheck all.
    if (!confirmDeleteAll) {
      setConfirmDeleteAll(true)
      return
    }

    try {
      await deleteAllItems()
      // calls useTravelItems.deleteAllItems() which DELETEs all rows in Supabase
    } catch (err) {
      alert('Error deleting all items: ' + (err.message || 'Unknown error'))
    } finally {
      setConfirmDeleteAll(false)
    }
  }

  const cancelDeleteAll = () => {
    setConfirmDeleteAll(false)
  }


  // ── STANDARD LIST HANDLERS ────────────────────────────────────

  const saveAsStandard = () => {
    // Snapshots the current live list into localStorage as the template.
    // Only saves item names (not IDs or completed state).

    if (travelItems.length === 0) {
      alert('Your packing list is empty. Add items first, then save as your standard list.')
      return
    }

    const names = travelItems.map(item => item.name)
    // extract just the name strings from the full item objects
    // e.g. ["Wig", "Corset", "Heels"]

    localStorage.setItem(STANDARD_LIST_KEY, JSON.stringify(names))
    // JSON.stringify converts the array to a string for localStorage
    // localStorage only stores strings, not objects

    setStandardItems(names)
    // update local state so the standard panel shows immediately

    alert(`Standard list saved with ${names.length} item${names.length !== 1 ? 's' : ''}! You can load it any time to rebuild your packing list.`)
  }

  const loadStandard = async () => {
    // Adds all standard items NOT already in the live list.
    // Skips duplicates (case-insensitive) so running this multiple times is safe.

    if (standardItems.length === 0) {
      alert('No standard list saved yet. Build your packing list and click "Save as Standard".')
      return
    }

    // Get the names already in the live list (lowercased for comparison)
    const existingNames = travelItems.map(item => item.name.toLowerCase())
    // existingNames = ["wig", "corset", "heels"] (all lowercase)

    const toAdd = standardItems.filter(name => !existingNames.includes(name.toLowerCase()))
    // keep only standard items that are NOT already in the live list
    // case-insensitive so "Wig" and "wig" are treated as the same

    if (toAdd.length === 0) {
      alert('All standard items are already in your list!')
      return
    }

    try {
      // Add each missing item one at a time.
      // await inside a for-of loop runs them in sequence to avoid race conditions.
      for (const name of toAdd) {
        await addItem(name)
      }
      alert(`Added ${toAdd.length} item${toAdd.length !== 1 ? 's' : ''} from your standard list.`)
    } catch (err) {
      alert('Error loading standard items: ' + (err.message || 'Unknown error'))
    }
  }

  const removeFromStandard = (indexToRemove) => {
    // Removes a single item from the saved standard list template.

    const updated = standardItems.filter((_, i) => i !== indexToRemove)
    // filter keeps everything EXCEPT the item at indexToRemove
    // _ = the item value (unused), i = its index

    setStandardItems(updated)
    localStorage.setItem(STANDARD_LIST_KEY, JSON.stringify(updated))
    // persist the updated template immediately
  }

  const clearStandard = () => {
    // Wipes the entire saved standard list after confirmation.

    if (!window.confirm('Clear your entire standard list template? This cannot be undone.')) return

    setStandardItems([])
    localStorage.removeItem(STANDARD_LIST_KEY)
    // removeItem deletes the key from localStorage entirely
  }


  // ── LOADING STATE ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="travel-page">
        <h1 className="page-title">
          Travel Checklist 
        </h1>
        <p>Loading your checklist...</p>
      </div>
    )
  }


  // ── DERIVED VALUES ────────────────────────────────────────────
  const packedCount = travelItems.filter(item => item.completed).length
  // count of items marked as packed -- recalculated every render


  // ── RENDER ────────────────────────────────────────────────────
  return (
    <div className="travel-page">

      <h1 className="page-title">
        Travel Checklist 
      </h1>

      {/* ── ADD ITEM INPUT ──────────────────────────────────────── */}
      <div className="travel-input-row">
        <input
          type="text"
          placeholder="Add an item... (e.g. Wig, Corset, Heels)"
          value={inputValue}
          // controlled input: React owns the value, not the browser
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          // press Enter to add without clicking the button
        />
        <button className="btn-primary" onClick={handleAdd}>
          <i className="bi bi-plus-circle"></i> Add
        </button>
      </div>


      {/* ── SUMMARY + BULK ACTIONS ROW ──────────────────────────── */}
      {/* FIX: wrapped in <> </> so the comment + div have a single JSX parent */}
      {travelItems.length > 0 && (
        <>
          {/* .travel-summary-row: flex space-between, wraps on mobile */}
          <div className="travel-summary-row">

            {/* Packed count -- .travel-summary--inline removes its own bottom margin */}
            <p className="travel-summary travel-summary--inline">
              <i className="bi bi-box-seam"></i>{' '}
              {packedCount} / {travelItems.length} packed
            </p>

            {/* Bulk action buttons */}
            {/* .travel-bulk-actions: flex row with gap, wraps */}
            <div className="travel-bulk-actions">

              {/* ── UNCHECK ALL (two-step) ─────────────────────────── */}
              {!confirmUncheck ? (
                <button
                  className="btn-secondary travel-bulk-btn"
                  onClick={handleUncheckAll}
                  title="Reset all checkboxes for a fresh packing session"
                >
                  <i className="bi bi-arrow-counterclockwise me-1"></i>
                  Uncheck All
                </button>
              ) : (
                // FIX: plain JS comment here (not JSX {/* */}) -- we're in a JS expression,
                // not inside a JSX element, so {/* */} would be parsed as an object literal
                // .travel-confirm-row: flex row with tight gap
                <div className="travel-confirm-row">
                  <span className="travel-confirm-text">Reset all?</span>
                  <button
                    className="btn-danger travel-bulk-btn"
                    onClick={handleUncheckAll}
                  >
                    Confirm
                  </button>
                  <button
                    className="btn-secondary travel-bulk-btn"
                    onClick={cancelUncheck}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* ── DELETE ALL (two-step) ──────────────────────────── */}
              {!confirmDeleteAll ? (
                <button
                  className="btn-danger travel-bulk-btn"
                  onClick={handleDeleteAll}
                  title="Delete every item from the list"
                >
                  <i className="bi bi-trash-fill me-1"></i>
                  Delete All
                </button>
              ) : (
                <div className="travel-confirm-row">
                  <span className="travel-confirm-text">Delete everything?</span>
                  <button
                    className="btn-danger travel-bulk-btn"
                    onClick={handleDeleteAll}
                  >
                    Confirm
                  </button>
                  <button
                    className="btn-secondary travel-bulk-btn"
                    onClick={cancelDeleteAll}
                  >
                    Cancel
                  </button>
                </div>
              )}

            </div>
          </div>
        </>
      )}

      {/* Summary line when list is empty */}
      {travelItems.length === 0 && (
        <p className="travel-summary">
          <i className="bi bi-box-seam"></i> 0 / 0 packed
        </p>
      )}


      {/* ── EMPTY STATE ─────────────────────────────────────────── */}
      {travelItems.length === 0 && (
        <p className="travel-empty-state">
          <i className="bi bi-bag-x"></i> No items yet. Add something above!
        </p>
      )}


      {/* ── TRAVEL ITEM LIST ────────────────────────────────────── */}
      <div className="travel-list">
        {travelItems.map(item => (
          <div
            key={item.id}
            // key = unique React identifier for this list item
            className={`travel-item ${item.completed ? 'completed' : ''}`}
            // 'completed' class = your CSS can add strikethrough, opacity, etc.
          >
            <span
              onClick={() => toggleCompleted(item.id)}
              // clicking the text/icon toggles the packed state
              style={{ textDecoration: item.completed ? 'line-through' : 'none' }}
            >
              <i className={`bi ${item.completed ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>{' '}
              {item.name}
            </span>

            <button
              className="btn-danger"
              onClick={() => deleteItem(item.id)}
            >
              <i className="bi bi-trash-fill"></i> Delete
            </button>
          </div>
        ))}
      </div>


      {/* ── STANDARD LIST SECTION ───────────────────────────────── */}
      {/* .travel-standard-section: top border + top padding */}
      <div className="travel-standard-section">

        {/* Toggle header */}
        {/* .travel-standard-toggle: ghost button, pink text */}
        <button
          onClick={() => setShowStandardPanel(!showStandardPanel)}
          className="travel-standard-toggle"
        >
          <i className={`bi ${showStandardPanel ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
          <i className="bi bi-bookmark-star-fill"></i>
          Standard List
          {standardItems.length > 0 && (
            // .travel-standard-badge: pink pill with count
            <span className="travel-standard-badge">
              {standardItems.length}
            </span>
          )}
        </button>

        {/* Panel body -- only rendered when open */}
        {showStandardPanel && (
          <div className="travel-standard-body">

            {/* Explanation */}
            <p className="travel-standard-hint">
              Your standard list is a reusable template of items you always pack.
              Save your current list as a template, then load it any time to quickly rebuild your checklist.
            </p>

            {/* Action buttons row */}
            <div className="travel-standard-actions">
              <button
                className="btn-primary"
                onClick={saveAsStandard}
                disabled={travelItems.length === 0}
                // disabled when there's nothing to save
              >
                <i className="bi bi-bookmark-plus me-1"></i>
                Save Current List as Standard
              </button>

              <button
                className="btn-secondary"
                onClick={loadStandard}
                disabled={standardItems.length === 0}
                // disabled when no standard list exists yet
              >
                <i className="bi bi-download me-1"></i>
                Load Standard into Packing List
              </button>
            </div>

            {/* Standard item list -- shown when template has items */}
            {standardItems.length > 0 && (
              <>
                <p className="travel-standard-list-title">
                  Saved template ({standardItems.length} item{standardItems.length !== 1 ? 's' : ''}):
                </p>
                {/* .travel-standard-list: plain list with column flex */}
                <ul className="travel-standard-list">
                  {standardItems.map((name, index) => (
                    <li
                      key={index}
                      // index is acceptable here since standard items don't reorder
                      className="travel-standard-item"
                    >
                      <span>
                        <i className="bi bi-bookmark-check me-2" style={{ color: 'var(--brand-primary)' }}></i>
                        {name}
                      </span>
                      <button
                        onClick={() => removeFromStandard(index)}
                        // remove just this one item from the template
                        title={`Remove "${name}" from standard list`}
                        className="travel-standard-remove-btn"
                      >
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Clear entire standard list */}
                <button
                  onClick={clearStandard}
                  className="travel-clear-standard-btn"
                >
                  <i className="bi bi-trash me-1"></i>
                  Clear standard list template
                </button>
              </>
            )}

            {/* Empty state for the standard panel */}
            {standardItems.length === 0 && (
              <p className="travel-standard-empty">
                No standard list saved yet. Add your usual items above and click "Save Current List as Standard".
              </p>
            )}
          </div>
        )}
      </div>

    </div>
  )
}

export default Travel